/**
 * Owned locations API wrapper (v2 — Project Independence, DNKADM-0051).
 * DNKADM-0374: adds `billingAnchorDay` (billing day override, 1–28) on both
 * V2Location (read) and LocationInput (write). PUT sentinel: `0` = clear,
 * `1`–`28` = set, omit = leave unchanged. POST: send only when filled (1–28).
 * GET: key absent when unset — `0` on the wire is also treated as absent.
 * Never JSON-null this field (other location optionals null-clear; this does not).
 *
 * The owned `v2_location` rows replace the v1 GHL service-locations proxy
 * (`getServiceLocations` in `operatingHours.ts`, retained for the not-yet-migrated
 * v1 features). This module is the source of the **owned `v2_location.id`** the
 * whole v2 admin surface scopes by (handoff §4.6): a real owned id, never a GHL
 * one. Bearer-only `/v2/admin/**` surface — `request()` routes it (DNKADM-0049
 * `isV2Surface`): bearer token, NO CSRF, NO cookies, the bounded 401→refresh→retry;
 * the mutating-audit `X-Session-Mode` stamp fires on the writes (`isV2AdminSurface`).
 *
 * **Endpoints (handoff §5.2, §3.1 — standard REST + soft-delete):**
 *  - `GET    /v2/admin/locations?status=active|inactive|all` → {@link V2Location}`[]`.
 *  - `GET    /v2/admin/locations/{id}` → one {@link V2Location}.
 *  - `POST   /v2/admin/locations` ← {@link LocationInput} → the created row.
 *  - `PUT    /v2/admin/locations/{id}` ← {@link LocationInput} → the updated row.
 *  - `POST   /v2/admin/locations/{id}/deactivate` | `/reactivate` (soft-delete /
 *    reactivate, §4.3) → the updated row.
 *
 * **`v2_location` field set (real contract — DNKAPI-0143, 2026-06-26; supersedes the
 * DNKADM-0051 *assumed* structured-address shape):** `id`, `name`, `timezone` (IANA —
 * governs all slot/day math), `isActive`, plus optional `phone`, a **single free-text
 * `address`** line/block (NOT split into street/city/state/zip), and a single optional
 * free-text **`mapUrl`** (DNKAPI-0164) — an arbitrary URL the consumer renders as a
 * "map / directions" link, parsed exactly like `address`. The structured
 * `addressLine1`/`addressLine2`/`city`/`state`/`postalCode` fields built to the 0051
 * assumption are removed — the backend never carried them. DNKAPI-0421/0422 added
 * `isDefault` (a **primitive boolean** on the write DTO — always sent back, see
 * `toBody`) and the independently nullable `resourceNounSingular`/`resourceNounPlural`
 * display strings (DNKADM-0267). Every field is parsed
 * DEFENSIVELY (mirrors `tenantSettings.ts` / `membershipSettings.ts`): a
 * missing/wrong-typed field degrades (an entry with no usable `id` is dropped), never
 * a throw.
 *
 * **Geo → `mapUrl` transition (DNKADM-0081 / DNKAPI-0164, not yet shipped):** the old
 * geo trio `latitude` / `longitude` / `googlePlaceId` is replaced by `mapUrl`. The
 * write path sends `mapUrl` ONLY (no coords). The read path PREFERS `mapUrl`, but
 * **tolerantly still parses the old coords** into deprecated `V2Location` fields used
 * **only** for the `LocationDetail` Directions fallback while the live API predates
 * 0164. Once 0164 ships the coords stop arriving and the fallback is dead — a tiny
 * later cleanup deletes the coord read + the deprecated fields.
 *
 * The fine-grained v2 error code is read with `v2ErrorCode()` (`./v2Errors`); the
 * human text comes from `serverMessage()`.
 */
import { request } from './client';
import type { ActiveFilterValue } from '@/hooks/useActiveFilter';

/** v2 admin owned-locations base path (bearer surface). */
const PATH = '/v2/admin/locations';

/**
 * One owned `v2_location` row. `id` is the owned location scope passed everywhere
 * downstream (§4.6). `timezone` is the IANA zone that governs the location's
 * slot/day math (§4.5). The optional fields are absent when the server carries none
 * (`exactOptionalPropertyTypes`: the key is omitted, never `undefined`). `address`
 * is a single free-text line; `mapUrl` is the single optional free-text map link
 * (DNKAPI-0164) the customer/detail renders as a map/Directions link.
 */
export interface V2Location {
  id: string;
  name: string;
  /** IANA timezone (e.g. `America/New_York`) — governs the location's slot/day math. */
  timezone: string;
  /** Soft-delete flag (§4.3); `false` = deactivated. */
  isActive: boolean;
  /** Single free-text address line/block (NOT split into street/city/state/zip). */
  address?: string;
  /**
   * Single optional free-text map link (DNKAPI-0164) — an arbitrary URL (typically a
   * Google Maps share link) the detail renders as a safe new-tab map link. Freeform,
   * unvalidated; parsed like `address`.
   */
  mapUrl?: string;
  /**
   * @deprecated DNKADM-0081 transition only — the pre-0164 API still sends the old geo
   * coords; read tolerantly so {@link V2Location} can fall back to a coord-built
   * Directions link until `mapUrl` arrives. Delete once DNKAPI-0164 is live.
   */
  latitude?: number;
  /** @deprecated DNKADM-0081 transition only — see {@link V2Location.latitude}. */
  longitude?: number;
  /** @deprecated DNKADM-0081 transition only — see {@link V2Location.latitude}. */
  googlePlaceId?: string;
  phone?: string;
  /**
   * Display/preselection-only default-location flag (DNKAPI-0421/0422, DNKADM-0267).
   * A backend **primitive boolean** — always present on the GET; parsed with a
   * `false` fallback. Read here so every update can send the current value back
   * (see {@link LocationInput.isDefault}).
   */
  isDefault: boolean;
  /**
   * What this location calls one bookable resource (e.g. a court, a room), or absent
   * when unset — the consumer app falls back to its own default noun (DNKADM-0267).
   */
  resourceNounSingular?: string;
  /**
   * The plural of {@link V2Location.resourceNounSingular}, entered explicitly —
   * NEVER derived by appending "s" (DNKADM-0267). Independent of the singular.
   */
  resourceNounPlural?: string;
  /**
   * Billing day of month override for memberships sold at this location
   * (DNKADM-0374, DNKAPI-0531). Absent when not set — GET never returns `0`;
   * any `0` or out-of-range value on the wire is treated as absent. `1`–`28`
   * overrides the tenant billing day for this location's membership sales.
   */
  billingAnchorDay?: number;
}

/**
 * Create/update body for a location. `name` + `timezone` are required by the form;
 * the rest are optional. `mapUrl` is the single optional free-text map link
 * (DNKAPI-0164) — no coords are sent. `isActive` is NOT settable here — activation is
 * the dedicated deactivate/reactivate endpoints (§4.3).
 */
export interface LocationInput {
  name: string;
  timezone: string;
  address?: string;
  mapUrl?: string;
  phone?: string;
  /**
   * REQUIRED — `LocationRequest.isDefault` is a backend **primitive boolean**
   * (DNKADM-0267): an omitted key deserializes to `false` server-side, which
   * silently unset the tenant's default location on every partial edit. Every
   * create/update must therefore carry the current value; {@link toBody} always
   * writes it, never conditionally.
   */
  isDefault: boolean;
  /** Nullable on the wire — omitted when unset (the backend keeps it null). */
  resourceNounSingular?: string;
  /** Entered explicitly, never derived from the singular (DNKADM-0267). */
  resourceNounPlural?: string;
  /**
   * Billing anchor day override (DNKADM-0374). Sentinel: `0` = clear;
   * `1`–`28` = set; omit when undefined (never JSON-null). PUT: always send
   * (mirrors `isDefault`); POST: only when filled.
   */
  billingAnchorDay?: number;
}

/** Options for {@link listLocations}. */
export interface ListLocationsOptions {
  /** Active/Inactive/All filter (§4.3); defaults to `active` (live rows only). */
  status?: ActiveFilterValue;
}

/**
 * A minimal assigned-location summary (`{ id, name }`) carried inline on a
 * Resources/Staff LIST row (DNKADM-0078, `HANDOFF-dink-api-list-row-locations`).
 * Shared by `V2Resource` / `V2Staff` so the list screens can render the
 * assigned-Locations column without a per-row fan-out.
 */
export interface LocationSummary {
  id: string;
  name: string;
}

// --- Defensive parsing helpers (mirrors tenantSettings.ts / membershipSettings.ts) ---

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * A stable string id from a NUMBER or a string (DNKADM-0064): v2 entity ids arrive
 * as numbers on the wire (`{ "id": 1, … }`) and are normalized to the `string` the
 * UI/router use — a finite number → its `String(n)` form, a non-empty string →
 * trimmed as-is, anything else → null. Preserves genuinely-string ids (it accepts
 * both), so a real string id is never force-coerced and a numeric id is never
 * dropped.
 */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/**
 * A finite number, or null. **Range-tolerant** — an out-of-range value (a latitude
 * past ±90, etc.) still parses; the create/edit form is the authority on ranges
 * (DNKADM-0066). Only a non-number or a non-finite value (`NaN`/`Infinity`) degrades.
 */
function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * A boolean from a real boolean, or a fallback when absent/unparseable. `isActive`
 * defaults to `true` on read (a row the server returned without the flag is treated
 * as live — the list endpoint returns live rows by default, §4.3).
 */
function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Assign an optional contact field onto `target` only when it parses to a non-empty
 * string — keeps the optional key absent (not `undefined`) per
 * `exactOptionalPropertyTypes`.
 */
function assignOptionalText(
  target: Record<string, unknown>,
  key: string,
  value: unknown,
): void {
  const text = asText(value);
  if (text !== null) target[key] = text;
}

/**
 * Billing anchor day (DNKADM-0374): integer in 1–28; `0` and anything else →
 * `null` (key omitted on read). GET never returns `0`; tolerated defensively.
 */
function asBillingAnchorDay(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const t = Math.trunc(value);
  return t >= 1 && t <= 28 ? t : null;
}

/**
 * Assign an optional numeric field onto `target` only when it parses to a finite
 * number — keeps the optional key absent (not `undefined`) per
 * `exactOptionalPropertyTypes`. Range-tolerant (see {@link asNumber}).
 */
function assignOptionalNumber(
  target: Record<string, unknown>,
  key: string,
  value: unknown,
): void {
  const num = asNumber(value);
  if (num !== null) target[key] = num;
}

/**
 * Parse one raw location, or null when it has no usable `id` (an entry with no
 * selectable id is dropped rather than surfaced as a half-identified option). A
 * missing `name`/`timezone` degrades to an empty string (the row still renders; the
 * server is the authority on the real value).
 */
function parseLocation(raw: unknown): V2Location | null {
  if (!isObject(raw)) return null;
  const id = asId(raw['id']);
  if (id === null) return null;

  // Build the required fields then layer the optional address/geo/contact fields onto
  // a record accumulator (assigned only when present — exactOptionalPropertyTypes).
  const acc: Record<string, unknown> = {};
  assignOptionalText(acc, 'address', raw['address']);
  assignOptionalText(acc, 'mapUrl', raw['mapUrl']);
  // DNKADM-0081 transition: prefer `mapUrl`, but still read the pre-0164 geo coords so
  // LocationDetail can fall back to a coord-built Directions link until 0164 ships.
  assignOptionalNumber(acc, 'latitude', raw['latitude']);
  assignOptionalNumber(acc, 'longitude', raw['longitude']);
  assignOptionalText(acc, 'googlePlaceId', raw['googlePlaceId']);
  assignOptionalText(acc, 'phone', raw['phone']);
  // Resource nouns (DNKADM-0267) — independently nullable display strings.
  assignOptionalText(acc, 'resourceNounSingular', raw['resourceNounSingular']);
  assignOptionalText(acc, 'resourceNounPlural', raw['resourceNounPlural']);
  // Billing anchor day override (DNKADM-0374) — absent when unset (0 on wire = unset).
  const billingDay = asBillingAnchorDay(raw['billingAnchorDay']);
  if (billingDay !== null) acc['billingAnchorDay'] = billingDay;
  return {
    id,
    name: asText(raw['name']) ?? '',
    timezone: asText(raw['timezone']) ?? '',
    // The wire flag is `active` (DNKADM-0064); tolerate legacy `isActive`.
    isActive: asBoolean(raw['active'] ?? raw['isActive'], true),
    // Backend primitive (DNKADM-0267) — always emitted on the GET; `false` fallback.
    isDefault: asBoolean(raw['isDefault'], false),
    ...(acc as Partial<V2Location>),
  };
}

/**
 * Extract the locations array TOLERANTLY from a successful body: a bare array, or
 * wrapped under a `locations`/`content` key (the wrapper-key shapes the other v2
 * reads use). Returns `[]` for anything that yields no array (an empty list is a
 * real first-run state, §8 — not an error to fail-closed on, unlike the v1 GHL
 * proxy).
 */
function extractLocations(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body)) {
    if (Array.isArray(body['locations'])) return body['locations'];
    if (Array.isArray(body['content'])) return body['content'];
  }
  return [];
}

/**
 * Parse the OPTIONAL inline assigned-locations summary off a Resources/Staff LIST
 * row (DNKADM-0078, `HANDOFF-dink-api-list-row-locations`), TOLERANTLY and
 * forward-compatibly. Accepts the preferred `locations: { id, name }[]` (a bare
 * `id` is tolerated too), OR an ids-only `locationIds: string[]` carried as
 * `{ id, name: id }` for the screen to render (an unresolved id shows the id).
 * Entries with no usable `id` are dropped; a missing `name` degrades to the `id`.
 *
 * **CRITICAL absent-vs-empty distinction:** when NEITHER key is present the field
 * is UNKNOWN → returns `undefined` (the screen shows a neutral "—"); an explicit
 * empty array is a real "assigned to no locations" → returns `[]` (the screen
 * shows red "No locations"). Absent ≠ empty — never a false red.
 */
export function parseRowLocations(raw: unknown): LocationSummary[] | undefined {
  if (!isObject(raw)) return undefined;
  const fromObjects = raw['locations'];
  if (Array.isArray(fromObjects)) {
    const out: LocationSummary[] = [];
    for (const entry of fromObjects) {
      const id = isObject(entry) ? asId(entry['id']) : asId(entry);
      if (id === null) continue;
      const name = isObject(entry) ? asText(entry['name']) : null;
      out.push({ id, name: name ?? id });
    }
    return out;
  }
  const fromIds = raw['locationIds'];
  if (Array.isArray(fromIds)) {
    const out: LocationSummary[] = [];
    for (const entry of fromIds) {
      const id = asId(entry);
      if (id !== null) out.push({ id, name: id });
    }
    return out;
  }
  return undefined;
}

/**
 * Build the JSON body for a create/update, omitting absent optional keys. `mapUrl`
 * (DNKAPI-0164) is sent only when set (omitted when blank, `exactOptionalPropertyTypes`);
 * no coordinates are ever sent.
 *
 * **`isDefault` is ALWAYS written** (DNKADM-0267): the backend field is a primitive
 * `boolean`, so an omitted key deserializes to `false` and silently unsets the
 * default location on an unrelated edit. Never make this conditional.
 */
function toBody(input: LocationInput): Record<string, unknown> {
  const json: Record<string, unknown> = {
    name: input.name,
    timezone: input.timezone,
    isDefault: input.isDefault,
  };
  if (input.address !== undefined) json['address'] = input.address;
  if (input.mapUrl !== undefined) json['mapUrl'] = input.mapUrl;
  if (input.phone !== undefined) json['phone'] = input.phone;
  if (input.resourceNounSingular !== undefined) {
    json['resourceNounSingular'] = input.resourceNounSingular;
  }
  if (input.resourceNounPlural !== undefined) {
    json['resourceNounPlural'] = input.resourceNounPlural;
  }
  // Billing anchor day (DNKADM-0374): 0 = clear, 1–28 = set, omit = leave unchanged.
  // Never JSON-null — other optionals null-clear; this field does not.
  // PUT: always sent (caller sets 0 or day); POST: only when filled (caller omits when blank).
  if (input.billingAnchorDay !== undefined) json['billingAnchorDay'] = input.billingAnchorDay;
  return json;
}

// --- Endpoints -----------------------------------------------------------------

/**
 * `GET /v2/admin/locations?status=…` → the tenant's owned locations, defensively
 * parsed (a bad entry is dropped). The `status` filter (§4.3) defaults to `active`
 * (live rows only); pass `inactive`/`all` to include deactivated rows. Throws a
 * typed `ApiError` on a non-2xx response.
 */
export async function listLocations(
  options: ListLocationsOptions = {},
): Promise<V2Location[]> {
  const status = options.status ?? 'active';
  const body = await request<unknown>('GET', PATH, { query: { status } });
  return extractLocations(body)
    .map(parseLocation)
    .filter((l): l is V2Location => l !== null);
}

/**
 * `GET /v2/admin/locations/{id}` → one owned location (defensively parsed). Throws
 * a typed `ApiError` (`404` NOT_FOUND, upstream forwarded).
 */
export async function getLocation(id: string): Promise<V2Location | null> {
  const body = await request<unknown>('GET', `${PATH}/${encodeURIComponent(id)}`);
  return parseLocation(body);
}

/**
 * `POST /v2/admin/locations` → create an owned location. Returns the re-parsed
 * created row. Throws a typed `ApiError` (`400` VALIDATION inline-able, `409`
 * CONFLICT, upstream).
 */
export async function createLocation(input: LocationInput): Promise<V2Location | null> {
  const body = await request<unknown>('POST', PATH, { json: toBody(input) });
  return parseLocation(body);
}

/**
 * `PUT /v2/admin/locations/{id}` → update an owned location. Returns the re-parsed
 * updated row. Throws a typed `ApiError`.
 */
export async function updateLocation(
  id: string,
  input: LocationInput,
): Promise<V2Location | null> {
  const body = await request<unknown>('PUT', `${PATH}/${encodeURIComponent(id)}`, {
    json: toBody(input),
  });
  return parseLocation(body);
}

/**
 * `POST /v2/admin/locations/{id}/deactivate` → soft-deactivate a location (§4.3 —
 * never a hard delete). Throws a typed `ApiError`.
 */
export async function deactivateLocation(id: string): Promise<void> {
  await request<unknown>('POST', `${PATH}/${encodeURIComponent(id)}/deactivate`);
}

/**
 * `POST /v2/admin/locations/{id}/reactivate` → reactivate a soft-deactivated
 * location (§4.3). Throws a typed `ApiError`.
 */
export async function reactivateLocation(id: string): Promise<void> {
  await request<unknown>('POST', `${PATH}/${encodeURIComponent(id)}/reactivate`);
}
